import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  FileCheck,
  Clock,
  Smartphone,
  Shield,
  Zap,
  Calendar,
  Download,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Users,
  TrendingUp,
  Lock,
  Star,
  ChevronRight,
  Menu,
  X, DoorOpen, LogIn
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import emailjs from 'emailjs-com';

// Style constants for consistency
const colors = {
  primary: '#10b981',
  primaryDark: '#0ea068',
  primaryLight: '#00d4aa',
  dark: '#0a0e1a',
  darker: '#0f1629',
  white: '#ffffff',
  gray: 'rgba(255, 255, 255, 0.7)',
  grayLight: 'rgba(255, 255, 255, 0.5)',
  error: '#ef4444',
  warning: '#fbbf24',
  border: 'rgba(255, 255, 255, 0.08)',
};

const ComparisonCard = ({ icon, before, after, isMobile }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        border: `1px solid ${isHovered ? 'rgba(16, 185, 129, 0.5)' : 'rgba(255, 255, 255, 0.08)'}`,
        borderRadius: '12px',
        padding: isMobile ? '24px' : '32px',
        height: '100%',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        boxShadow: isHovered ? '0 20px 40px rgba(16, 185, 129, 0.2)' : 'none',
      }}
    >
      <div style={{
        width: isMobile ? '48px' : '56px',
        height: isMobile ? '48px' : '56px',
        borderRadius: '12px',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: isMobile ? '20px' : '24px',
      }}>
        {React.cloneElement(icon, { size: isMobile ? 24 : 28, color: colors.primary })}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '20px' : '24px' }}>
        <div>
          <span style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            color: colors.error,
            fontSize: '11px',
            fontWeight: '700',
            padding: '4px 12px',
            borderRadius: '4px',
            display: 'inline-block',
            marginBottom: '12px',
            letterSpacing: '1px',
          }}>
            BEFORE OAK
          </span>
          <p style={{
            color: colors.gray,
            fontSize: isMobile ? '14px' : '15px',
            lineHeight: '1.6',
            margin: 0,
          }}>
            {before}
          </p>
        </div>

        <div style={{
          height: '1px',
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
        }} />

        <div>
          <span style={{
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            color: colors.primary,
            fontSize: '11px',
            fontWeight: '700',
            padding: '4px 12px',
            borderRadius: '4px',
            display: 'inline-block',
            marginBottom: '12px',
            letterSpacing: '1px',
          }}>
            AFTER OAK
          </span>
          <p style={{
            color: colors.white,
            fontSize: isMobile ? '14px' : '15px',
            lineHeight: '1.6',
            margin: 0,
            fontWeight: '500',
          }}>
            {after}
          </p>
        </div>
      </div>
    </div>
  );
};

const FeatureCard = ({ emoji, title, description, isMobile }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
        border: `1px solid ${isHovered ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.08)'}`,
        borderRadius: '12px',
        padding: isMobile ? '20px' : '24px',
        height: '100%',
        transition: 'all 0.3s ease',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
      }}
    >
      <div style={{ fontSize: isMobile ? '28px' : '32px', marginBottom: '16px' }}>{emoji}</div>
      <h3 style={{
        fontSize: isMobile ? '16px' : '18px',
        fontWeight: '600',
        color: colors.white,
        marginBottom: '8px',
        margin: 0,
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: isMobile ? '13px' : '14px',
        color: colors.gray,
        lineHeight: '1.6',
        margin: 0,
      }}>
        {description}
      </p>
    </div>
  );
};

const ProcessStep = ({ step, icon, title, description, isMobile }) => {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: isMobile ? '20px' : '24px',
    }}>
      <div style={{
        width: isMobile ? '64px' : '72px',
        height: isMobile ? '64px' : '72px',
        borderRadius: '20px',
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: isMobile ? '24px' : '28px',
        fontWeight: '800',
        color: colors.white,
        boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)',
      }}>
        {step}
      </div>
      
      <div style={{
        width: isMobile ? '56px' : '64px',
        height: isMobile ? '56px' : '64px',
        borderRadius: '12px',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {React.cloneElement(icon, { size: isMobile ? 28 : 32, color: colors.primary })}
      </div>

      <h3 style={{
        fontSize: isMobile ? '18px' : '20px',
        fontWeight: '600',
        color: colors.white,
        margin: 0,
      }}>
        {title}
      </h3>
      <p style={{
        fontSize: isMobile ? '13px' : '14px',
        color: colors.gray,
        lineHeight: '1.6',
        maxWidth: '280px',
        margin: 0,
      }}>
        {description}
      </p>
    </div>
  );
};

const TestimonialCard = ({ quote, author, role, isMobile }) => {
  return (
    <div style={{
      backgroundColor: 'rgba(255, 255, 255, 0.03)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      borderRadius: '12px',
      padding: isMobile ? '24px' : '32px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: isMobile ? '20px' : '24px',
      }}>
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={isMobile ? 16 : 20} fill={colors.warning} color={colors.warning} />
        ))}
      </div>
      
      <p style={{
        fontSize: isMobile ? '14px' : '16px',
        fontStyle: 'italic',
        lineHeight: '1.7',
        color: 'rgba(255, 255, 255, 0.9)',
        flex: 1,
        margin: 0,
        marginBottom: isMobile ? '20px' : '24px',
      }}>
        "{quote}"
      </p>
      
      <div style={{
        paddingTop: isMobile ? '20px' : '24px',
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
      }}>
        <p style={{
          fontSize: isMobile ? '14px' : '16px',
          fontWeight: '600',
          color: colors.white,
          margin: 0,
          marginBottom: '4px',
        }}>
          {author}
        </p>
        <p style={{
          fontSize: isMobile ? '12px' : '13px',
          color: colors.gray,
          margin: 0,
        }}>
          {role}
        </p>
      </div>
    </div>
  );
};

const PricingCard = ({ title, subtitle, price, period, features, isPopular, onGetStarted, originalPrice, isMobile }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div style={{
      backgroundColor: isPopular
        ? `${colors.primary}`
        : 'rgba(255, 255, 255, 0.03)',
      background: isPopular
        ? `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`
        : 'rgba(255, 255, 255, 0.03)',
      border: `1px solid ${isPopular ? colors.primary : 'rgba(255, 255, 255, 0.08)'}`,
      borderRadius: '12px',
      padding: isMobile ? '32px 24px' : '40px',
      height: '100%',
      position: 'relative',
      transform: isPopular && !isMobile ? 'scale(1.05)' : 'scale(1)',
      boxShadow: isPopular ? '0 20px 60px rgba(16, 185, 129, 0.4)' : 'none',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {isPopular && (
        <div style={{
          position: 'absolute',
          top: '-14px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: colors.dark,
          color: colors.primary,
          fontWeight: '800',
          fontSize: isMobile ? '11px' : '12px',
          padding: isMobile ? '5px 16px' : '6px 20px',
          borderRadius: '20px',
          letterSpacing: '1px',
        }}>
          BEST VALUE
        </div>
      )}
      
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '24px' : '32px',
        flex: 1,
      }}>
        <div style={{ textAlign: 'center' }}>
          <h3 style={{
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: '700',
            color: isPopular ? colors.dark : colors.white,
            margin: 0,
            marginBottom: '8px',
          }}>
            {title}
          </h3>
          {subtitle && (
            <p style={{
              fontSize: isMobile ? '13px' : '14px',
              color: isPopular ? 'rgba(10, 14, 26, 0.8)' : colors.gray,
              margin: 0,
            }}>
              {subtitle}
            </p>
          )}
        </div>

        <div style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: isMobile ? '40px' : '48px',
            fontWeight: '800',
            color: isPopular ? colors.dark : colors.primary,
            margin: 0,
          }}>
            ${price}
          </h2>
          <p style={{
            fontSize: isMobile ? '13px' : '14px',
            color: isPopular ? 'rgba(10, 14, 26, 0.8)' : colors.gray,
            margin: 0,
          }}>
            per location / {period}
          </p>
          {originalPrice && (
            <p style={{
              fontSize: '12px',
              color: isPopular ? 'rgba(10, 14, 26, 0.6)' : colors.grayLight,
              textDecoration: 'line-through',
              marginTop: '8px',
              margin: 0,
            }}>
              Regular: ${originalPrice}/{period}
            </p>
          )}
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '12px' : '16px',
          flex: 1,
        }}>
          {features.map((feature, i) => (
            <div key={i} style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: isMobile ? '12px' : '16px',
            }}>
              <CheckCircle
                size={isMobile ? 18 : 20}
                style={{
                  color: isPopular ? colors.dark : colors.primary,
                  flexShrink: 0,
                  marginTop: '2px',
                }}
              />
              <p style={{
                fontSize: isMobile ? '13px' : '14px',
                color: isPopular ? colors.dark : colors.white,
                fontWeight: isPopular ? '500' : '400',
                margin: 0,
              }}>
                {feature}
              </p>
            </div>
          ))}
        </div>

        <button
          onClick={onGetStarted}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          style={{
            width: '100%',
            padding: isMobile ? '14px' : '16px',
            fontSize: isMobile ? '15px' : '16px',
            fontWeight: '600',
            borderRadius: '12px',
            border: isPopular ? 'none' : `2px solid ${colors.primary}`,
            backgroundColor: isPopular ? colors.dark : 'transparent',
            color: isPopular ? colors.primary : colors.primary,
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
            boxShadow: isHovered 
              ? isPopular 
                ? '0 10px 30px rgba(10, 14, 26, 0.3)'
                : '0 10px 30px rgba(16, 185, 129, 0.2)'
              : 'none',
          }}
        >
          Get Started {isPopular && '(Most Popular)'}
        </button>
      </div>
    </div>
  );
};

export default function ComplianceLandingPage() {
  const [email, setEmail] = useState('');
  const [shopName, setShopName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isMobile, setIsMobile] = useState(false);

  const navigate = useNavigate();

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleDemoRequest = (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    const emailData = {
      email: email,
      subject: `Demo Request - ${shopName}`,
      message: `
  🌿 OAK COMPLIANCE DEMO REQUEST 🌿
  -----------------------------------
  Dispensary Name: ${shopName}
  Contact Email: ${email}
  Request Date: ${new Date().toLocaleString()}

  Page: ${window.location.pathname}
  User Agent: ${navigator.userAgent}
  -----------------------------------
      `.trim()
    };

    emailjs.send('get_email', 'tem', emailData)
      .then(
        (result) => {
          console.log('Demo request sent successfully:', result);
          setStatus({
            type: 'success',
            message: 'Demo request submitted! We\'ll email you within 24 hours to schedule.'
          });
          setSubmitted(true);
          setEmail('');
          setShopName('');
          setLoading(false);
          
          setTimeout(() => {
            setSubmitted(false);
            setStatus({ type: '', message: '' });
          }, 5000);
        },
        (error) => {
          console.error('EmailJS Error:', error);
          setStatus({
            type: 'error',
            message: 'Unable to send demo request. Please try emailing us directly or try again.'
          });
          setLoading(false);
        }
      );
  };

  const scrollToDemo = () => {
    setMobileMenuOpen(false);
    document.getElementById('demo-form').scrollIntoView({ behavior: 'smooth' });
  };

  const [showNav, setShowNav] = useState(false);

  useEffect(() => {
    emailjs.init('gHDv2j1boFBdQTfm3');
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setShowNav(scrollPosition > 100);
      setIsAtTop(scrollPosition < 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (mobileMenuOpen && !e.target.closest('.hamburger-menu')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileMenuOpen]);

  // Container style for consistent max-width and centering
  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: isMobile ? '0 16px' : '0 24px',
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colors.dark,
      color: colors.white,
      fontFamily: '"Inter", system-ui, -apple-system, sans-serif',
    }}>

    {/* Hamburger Menu - Only visible at top of page */}
    <div
      className="hamburger-menu"
      style={{
        position: 'fixed',
        top: isMobile ? '16px' : '24px',
        right: isMobile ? '16px' : '24px',
        zIndex: 100,
        opacity: isAtTop ? 1 : 0,
        pointerEvents: isAtTop ? 'auto' : 'none',
        transition: 'opacity 0.3s ease',
      }}
    >
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        style={{
          width: isMobile ? '44px' : '48px',
          height: isMobile ? '44px' : '48px',
          borderRadius: '12px',
          background: 'rgba(10, 14, 26, 0.95)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${mobileMenuOpen ? colors.primary : 'rgba(255, 255, 255, 0.1)'}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          boxShadow: mobileMenuOpen ? `0 8px 20px rgba(16, 185, 129, 0.3)` : 'none',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = colors.primary;
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          if (!mobileMenuOpen) {
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
          }
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        {mobileMenuOpen ? (
          <X size={isMobile ? 20 : 24} color={colors.primary} />
        ) : (
          <Menu size={isMobile ? 20 : 24} color={colors.white} />
        )}
      </button>

      {/* Dropdown Menu */}
      {mobileMenuOpen && (
        <div
          style={{
            position: 'absolute',
            top: isMobile ? '52px' : '56px',
            right: '0',
            minWidth: isMobile ? '180px' : '200px',
            background: 'rgba(10, 14, 26, 0.98)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${colors.primary}`,
            borderRadius: '12px',
            padding: '8px',
            boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
            animation: 'slideDown 0.2s ease',
          }}
        >
          <button
            onClick={() => {
              navigate('/signup');
              setMobileMenuOpen(false);
            }}
            style={{
              width: '100%',
              padding: isMobile ? '10px 14px' : '12px 16px',
              background: 'transparent',
              border: 'none',
              borderRadius: '8px',
              color: colors.white,
              fontSize: isMobile ? '15px' : '16px',
              fontWeight: '500',
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(16, 185, 129, 0.1)';
              e.target.style.color = colors.primary;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = colors.white;
            }}
          >
            Sign Up
          </button>

          <button
            onClick={() => {
              navigate('/login');
              setMobileMenuOpen(false);
            }}
            style={{
              width: '100%',
              padding: isMobile ? '10px 14px' : '12px 16px',
              background: 'transparent',
              border: 'none',
              borderRadius: '8px',
              color: colors.white,
              fontSize: isMobile ? '15px' : '16px',
              fontWeight: '500',
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(16, 185, 129, 0.1)';
              e.target.style.color = colors.primary;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = colors.white;
            }}
          >
            Login
          </button>

          <button
            onClick={scrollToDemo}
            style={{
              width: '100%',
              padding: isMobile ? '10px 14px' : '12px 16px',
              background: 'transparent',
              border: 'none',
              borderRadius: '8px',
              color: colors.white,
              fontSize: isMobile ? '15px' : '16px',
              fontWeight: '500',
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'rgba(16, 185, 129, 0.1)';
              e.target.style.color = colors.primary;
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = colors.white;
            }}
          >
            Book a Demo
          </button>
        </div>
      )}
    </div>

    {/* Desktop Navigation Bar */}
    <nav style={{
      position: 'fixed',
      top: 0,
      width: '100%',
      background: 'rgba(10, 14, 26, 0.95)',
      backdropFilter: 'blur(20px)',
      zIndex: 50,
      borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
      transform: showNav ? 'translateY(0)' : 'translateY(-100%)',
      transition: 'transform 0.3s ease',
    }}>
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: isMobile ? '0.75rem 1rem' : '1rem 1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
        }}>
          <div style={{
            width: isMobile ? '2rem' : '2.5rem',
            height: isMobile ? '2rem' : '2.5rem',
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
            borderRadius: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
          }}>
            <img 
              src={`${process.env.PUBLIC_URL}/tree.png`} 
              alt="Green icon"
              style={{
                width: isMobile ? '1.25rem' : '1.5rem',
                height: isMobile ? '1.25rem' : '1.5rem',
              }}
            />
          </div>
          
          <h1 style={{
            fontSize: isMobile ? '1.125rem' : '1.5rem',
            fontWeight: '700',
            margin: 0,
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            Oak Compliance
          </h1>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '0.5rem' : '2rem',
        }}>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: isMobile ? '0.5rem 0.875rem' : '0.625rem 1.5rem',
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
              color: colors.white,
              border: 'none',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: isMobile ? '14px' : '16px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.3)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.boxShadow = 'none';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            Login
          </button>

          <button
            onClick={scrollToDemo}
            style={{
              padding: isMobile ? '0.5rem 0.875rem' : '0.625rem 1.5rem',
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
              color: colors.white,
              border: 'none',
              borderRadius: '12px',
              fontWeight: '600',
              fontSize: isMobile ? '14px' : '16px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={(e) => {
              e.target.style.boxShadow = '0 8px 20px rgba(16, 185, 129, 0.3)';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.boxShadow = 'none';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {isMobile ? 'DEMO' : 'GET DEMO'}
          </button>
        </div>
      </div>
    </nav>
      
      {/* HERO SECTION */}
      <section style={{
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(180deg, ${colors.dark} 0%, ${colors.darker} 100%)`,
        paddingTop: isMobile ? '30px' : '50px',
        paddingBottom: isMobile ? '30px' : '40px',
      }}>
        {/* Background gradient effect */}
        <div style={{
          position: 'absolute',
          top: '-50%',
          left: '-25%',
          width: '150%',
          height: '150%',
          background: `radial-gradient(circle at center, rgba(16, 185, 129, 0.15) 0%, transparent 70%)`,
          opacity: 0.7,
        }} />

        <div style={{ ...containerStyle, position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: isMobile ? '28px' : '40px',
          }}>
            <div style={{
              backgroundColor: colors.primary,
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
              color: colors.white,
              padding: isMobile ? '8px 20px' : '10px 24px',
              borderRadius: '30px',
              fontSize: isMobile ? '12px' : '14px',
              fontWeight: '600',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <Shield size={isMobile ? 14 : 16} />
              Built 100% for 935 CMR 500
            </div>

            <h1 style={{
              fontSize: isMobile ? '2rem' : 'clamp(2.5rem, 8vw, 5rem)',
              lineHeight: '1.3',
              maxWidth: '1000px',
              margin: 0,
              fontWeight: '800',
              letterSpacing: '-0.02em',
              background: `linear-gradient(135deg, ${colors.white} 0%, ${colors.primary} 50%, ${colors.primaryLight} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              Never Scramble for Last Thursday's Log Again
            </h1>

            <p style={{
              fontSize: isMobile ? '1rem' : 'clamp(1.1rem, 2.5vw, 1.5rem)',
              maxWidth: '750px',
              lineHeight: '1.5',
              color: colors.gray,
              margin: 0,
            }}>
              Your closing manager's new best friend. One-tap daily checklists, instant audit-ready PDFs, and no more digging for last Thursday's log.
            </p>
            <p style={{
              fontSize: isMobile ? '1rem' : 'clamp(1.1rem, 2.5vw, 1.5rem)',
              maxWidth: '750px',
              lineHeight: '1.5',
              color: colors.gray,
              margin: 0,
            }}>
              With 13 retail licenses surrendered in 2025 alone, don't let compliance be the reason yours is next.
            </p>
            
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '16px',
              width: isMobile ? '100%' : 'auto',
              maxWidth: isMobile ? '400px' : 'none',
            }}>
              <button
                onClick={() => navigate('/login')}
                style={{
                  background: 'transparent',
                  color: colors.primary,
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: '600',
                  padding: isMobile ? '16px 36px' : '20px 48px',
                  borderRadius: '12px',
                  border: `2px solid ${colors.primary}`,
                  cursor: 'pointer',
                  boxShadow: '0 10px 40px rgba(16, 185, 129, 0.1)',
                  transition: 'all 0.3s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: isMobile ? '100%' : 'auto',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.background = `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`;
                  e.target.style.color = colors.white;
                  e.target.style.boxShadow = '0 15px 50px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.background = 'transparent';
                  e.target.style.color = colors.primary;
                  e.target.style.boxShadow = '0 10px 40px rgba(16, 185, 129, 0.1)';
                }}
              >
                Login
                <DoorOpen size={isMobile ? 18 : 20} />
              </button>

              <button
                onClick={() => navigate('/signup')}
                style={{
                  background: 'transparent',
                  color: colors.primary,
                  fontSize: isMobile ? '16px' : '18px',
                  fontWeight: '600',
                  padding: isMobile ? '16px 36px' : '20px 48px',
                  borderRadius: '12px',
                  border: `2px solid ${colors.primary}`,
                  cursor: 'pointer',
                  boxShadow: '0 10px 40px rgba(16, 185, 129, 0.1)',
                  transition: 'all 0.3s ease',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: isMobile ? '100%' : 'auto',
                }}
                onMouseEnter={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.background = `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`;
                  e.target.style.color = colors.white;
                  e.target.style.boxShadow = '0 15px 50px rgba(16, 185, 129, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.background = 'transparent';
                  e.target.style.color = colors.primary;
                  e.target.style.boxShadow = '0 10px 40px rgba(16, 185, 129, 0.1)';
                }}
              >
                Register
                <LogIn size={isMobile ? 18 : 20} />
              </button>
            </div>

            <button
              onClick={scrollToDemo}
              style={{
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                color: colors.white,
                fontSize: isMobile ? '16px' : '18px',
                fontWeight: '600',
                padding: isMobile ? '16px 36px' : '20px 48px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 10px 40px rgba(16, 185, 129, 0.3)',
                transition: 'all 0.3s ease',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                width: isMobile ? '100%' : 'auto',
                maxWidth: isMobile ? '400px' : 'none',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 15px 50px rgba(16, 185, 129, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 10px 40px rgba(16, 185, 129, 0.3)';
              }}
            >
              {isMobile ? 'Book Demo' : 'Book a 10-Minute Demo'}
              <ArrowRight size={isMobile ? 18 : 20} />
            </button>
          </div>
        </div>
      </section>

      {/* PAIN POINTS SECTION */}
      <section style={{ 
        paddingTop: isMobile ? '48px' : '80px', 
        paddingBottom: isMobile ? '48px' : '80px' 
      }}>
        <div style={containerStyle}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '40px' : '64px',
          }}>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{
                fontSize: isMobile ? '1.75rem' : 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '700',
                margin: 0,
              }}>
                If This Sounds Familiar, Keep Reading
              </h2>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(500px, 1fr))',
              gap: isMobile ? '24px' : '32px',
            }}>
              {[
                {
                  icon: <AlertTriangle />,
                  before: 'Scrambling to find paper logs when CCC shows up',
                  after: 'Every checklist auto-saved, timestamped, and exportable in 3 seconds',
                },
                {
                  icon: <FileText />,
                  before: 'Managers forgetting to sign off on closing duties',
                  after: 'Digital signature captures who did what, when, every single night',
                },
                {
                  icon: <Clock />,
                  before: 'Spending 15+ minutes per shift on compliance busywork',
                  after: 'One-screen checklist. Tap. Done. PDF generates automatically.',
                },
                {
                  icon: <Users />,
                  before: 'Training new staff on 37-page compliance binders',
                  after: "Pull up Oak on any phone. They'll figure it out in 90 seconds.",
                },
              ].map((item, index) => (
                <ComparisonCard key={index} {...item} isMobile={isMobile} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section style={{
        background: `linear-gradient(180deg, rgba(16, 185, 129, 0.05) 0%, transparent 100%)`,
        paddingTop: isMobile ? '48px' : '80px',
        paddingBottom: isMobile ? '48px' : '80px',
      }}>
        <div style={containerStyle}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '40px' : '64px',
          }}>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{
                fontSize: isMobile ? '1.75rem' : 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '700',
                margin: 0,
                marginBottom: '16px',
              }}>
                How It Works (Stupid Simple)
              </h2>
              <p style={{
                fontSize: isMobile ? '16px' : '18px',
                color: colors.gray,
                maxWidth: '600px',
                margin: '0 auto',
              }}>
                No training manuals. No consultant calls. Just compliance that works.
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: isMobile ? '40px' : '32px',
            }}>
              {[
                {
                  step: '1',
                  icon: <Smartphone />,
                  title: 'Open Oak on Any Device',
                  description: "Phone, tablet, ancient iPad—doesn't matter. Pull up compliance.oak-us.com and log in.",
                },
                {
                  step: '2',
                  icon: <CheckCircle2 />,
                  title: 'Tap Tasks as You Go',
                  description: 'Opening, Mid-Day, Closing, As-Needed. Each section pulls straight from 935 CMR 500.',
                },
                {
                  step: '3',
                  icon: <Download />,
                  title: 'Export Signed PDF Instantly',
                  description: 'One button. Manager signs. Timestamped PDF downloads. Email it or keep it in the cloud.',
                },
              ].map((item, index) => (
                <ProcessStep key={index} {...item} isMobile={isMobile} />
              ))}
            </div>

            {/* PDF Preview Box */}
            <div style={{
              marginTop: isMobile ? '24px' : '32px',
              padding: isMobile ? '32px 24px' : '40px',
              background: `linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)`,
              border: '2px dashed rgba(16, 185, 129, 0.3)',
              borderRadius: '12px',
              textAlign: 'center',
            }}>
              <FileCheck size={isMobile ? 40 : 48} style={{ marginBottom: '20px', color: colors.primary }} />
              <h3 style={{
                fontSize: isMobile ? '20px' : '24px',
                fontWeight: '600',
                marginBottom: '16px',
                margin: 0,
              }}>
                Actual PDF Preview
              </h3>
              <p style={{
                color: colors.gray,
                fontSize: isMobile ? '14px' : '16px',
                maxWidth: '600px',
                margin: '0 auto',
                marginBottom: '24px',
              }}>
                Each exported report includes shop name, date, completed tasks with timestamps, manager signature, and audit reference number.
              </p>
              <a 
                href={`${process.env.PUBLIC_URL}/compliance-daily-2025-11-18.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontFamily: 'monospace',
                  backgroundColor: 'rgba(16, 185, 129, 0.15)',
                  color: colors.primary,
                  fontWeight: '600',
                  fontSize: isMobile ? '12px' : '14px',
                  padding: isMobile ? '12px 24px' : '16px 32px',
                  borderRadius: '8px',
                  display: 'inline-block',
                  textDecoration: 'none',
                  transition: 'all 0.3s ease',
                  wordBreak: 'break-all',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(16, 185, 129, 0.25)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(16, 185, 129, 0.15)';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                View Sample PDF: compliance-daily-2025-11-18.pdf
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section style={{ 
        paddingTop: isMobile ? '48px' : '80px', 
        paddingBottom: isMobile ? '48px' : '80px' 
      }}>
        <div style={containerStyle}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? '40px' : '64px',
          }}>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{
                fontSize: isMobile ? '1.75rem' : 'clamp(2rem, 4vw, 3rem)',
                fontWeight: '700',
                margin: 0,
                marginBottom: '16px',
              }}>
                Everything You Need, Nothing You Don't
              </h2>
              <p style={{
                fontSize: isMobile ? '16px' : '18px',
                color: colors.gray,
                maxWidth: '700px',
                margin: '0 auto',
              }}>
                Built by a Massachusetts GM who got tired of failing mock audits
              </p>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: isMobile ? '16px' : '24px',
            }}>
              {[
                { emoji: '📱', title: 'Works on Any Device', description: 'Responsive design that actually works' },
                { emoji: '⚡', title: 'Zero Training Required', description: 'If you can use Instagram, you can use Oak' },
                { emoji: '🔒', title: 'Audit-Ready PDFs', description: 'Signed, timestamped, regulation-referenced reports' },
                { emoji: '📅', title: 'Daily + As-Needed', description: 'Opening, Mid-Day, Closing, plus one-offs' },
                { emoji: '🌱', title: 'Built for 935 CMR 500', description: 'Every task mapped to Mass adult-use regs' },
                { emoji: '🔄', title: 'Metrc Integration', description: 'Inventory reconciliation coming soon' },
                { emoji: '⏱️', title: 'Save 15+ Min/Shift', description: 'No more binders or Google Sheets' },
                { emoji: '👥', title: 'Multi-Location Ready', description: 'Manage 1 shop or 5 from one dashboard' },
              ].map((feature, index) => (
                <FeatureCard key={index} {...feature} isMobile={isMobile} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section style={{
        backgroundColor: colors.darker,
        padding: isMobile ? '60px 20px' : '100px 20px',
      }}>
        <div style={containerStyle}>
          <h2 style={{
            fontSize: isMobile ? '1.75rem' : 'clamp(2.5rem, 5vw, 3.5rem)',
            fontWeight: 800,
            textAlign: 'center',
            marginBottom: '24px',
            background: `linear-gradient(135deg, ${colors.white} 0%, ${colors.primary} 70%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            We asked 47 Massachusetts dispensaries:<br />
            "What sucks most about daily compliance?"
          </h2>

          <p style={{
            textAlign: 'center',
            fontSize: isMobile ? '1rem' : '1.25rem',
            color: colors.gray,
            maxWidth: '800px',
            margin: '0 auto',
            marginBottom: isMobile ? '40px' : '64px',
          }}>
            Here are the top 3 answers — and exactly how Oak eliminates them.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(340px, 1fr))',
            gap: isMobile ? '24px' : '32px',
          }}>
            {[
              {
                headache: "Closing manager forgets half the checklist and we scramble when CCC shows up",
                fix: "One-screen checklist with Opening / Mid-Day / Closing sections — impossible to miss a task",
                result: "100% completion rate, audit-ready PDF in 3 seconds",
              },
              {
                headache: "Metrc reconciliation takes 30–60 min every night and someone always screws it up",
                fix: "Auto-sync with Metrc API (live in 30 days) + auto-checks the 'Reconcile inventory' task",
                result: "Closing time cut from 45 min → under 5 min",
              },
              {
                headache: "Paper binder or Google Sheet that nobody updates → $10k fine risk",
                fix: "Phone/tablet app that actually gets used + timestamped digital signatures",
                result: "Zero missing logs, zero stress when investigators walk in",
              },
            ].map((item, i) => (
              <div key={i} style={{
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                padding: isMobile ? '24px' : '32px',
                borderRadius: '16px',
                border: `1px solid rgba(255, 255, 255, 0.08)`,
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.borderColor = colors.primary}
              onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)'}
              >
                <div style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  color: '#fca5a5',
                  padding: isMobile ? '10px 16px' : '12px 20px',
                  borderRadius: '12px',
                  fontWeight: 600,
                  marginBottom: '20px',
                  fontSize: isMobile ? '0.95rem' : '1.1rem',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                }}>
                  {item.headache}
                </div>

                <h3 style={{
                  margin: '0 0 12px',
                  fontSize: isMobile ? '1.1rem' : '1.3rem',
                  color: colors.primary,
                  fontWeight: 600,
                }}>
                  Oak fixes it:
                </h3>
                <p style={{
                  margin: '0 0 16px',
                  color: colors.gray,
                  lineHeight: 1.6,
                  fontSize: isMobile ? '0.9rem' : '1rem',
                }}>
                  {item.fix}
                </p>

                <div style={{
                  background: `linear-gradient(135deg, ${colors.primary}20, ${colors.primaryLight}20)`,
                  color: colors.primary,
                  padding: isMobile ? '10px 16px' : '12px 20px',
                  borderRadius: '12px',
                  fontWeight: 600,
                  border: `1px solid ${colors.primary}40`,
                  fontSize: isMobile ? '0.9rem' : '1rem',
                }}>
                  {item.result}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section style={{
        background: `linear-gradient(180deg, ${colors.dark} 0%, ${colors.darker} 100%)`,
        paddingTop: isMobile ? '48px' : '80px',
        paddingBottom: isMobile ? '48px' : '80px',
      }}>
        <div style={{ 
          maxWidth: '700px', 
          margin: '0 auto', 
          padding: isMobile ? '0 16px' : '0 24px' 
        }}>
          <div
            id="demo-form"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
              border: '2px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '12px',
              padding: isMobile ? '32px 24px' : '56px 48px',
              textAlign: 'center',
            }}
          >
            <h3 style={{
              fontSize: isMobile ? '1.5rem' : 'clamp(1.75rem, 3vw, 2.5rem)',
              fontWeight: '700',
              marginBottom: '16px',
              margin: 0,
            }}>
              See It In Action (10 Minutes, Tops)
            </h3>

            <p style={{
              fontSize: isMobile ? '16px' : '18px',
              color: colors.gray,
              marginBottom: '40px',
              maxWidth: '600px',
              margin: '0 auto',
              marginTop: '16px',
              marginBottom: '40px',
              lineHeight: '1.6',
            }}>
              No sales pitch. Just a live walkthrough of the dashboard, checklist, and PDF export. We'll answer your Metrc questions, too.
            </p>

            {submitted && (
              <div style={{
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '32px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}>
                <CheckCircle size={20} color={colors.primary} />
                <p style={{ margin: 0, color: colors.white, fontSize: isMobile ? '14px' : '16px' }}>
                  Got it! We'll email you within 24 hours to schedule your demo.
                </p>
              </div>
            )}

            {status.message && (
              <div style={{
                backgroundColor: status.type === 'success' 
                  ? 'rgba(16, 185, 129, 0.1)' 
                  : 'rgba(239, 68, 68, 0.1)',
                border: `1px solid ${status.type === 'success' 
                  ? 'rgba(16, 185, 129, 0.3)' 
                  : 'rgba(239, 68, 68, 0.3)'}`,
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                flexWrap: 'wrap',
              }}>
                {status.type === 'success' ? (
                  <CheckCircle size={20} color={colors.primary} />
                ) : (
                  <AlertTriangle size={20} color={colors.error} />
                )}
                <p style={{ 
                  margin: 0, 
                  color: status.type === 'success' ? colors.white : colors.error,
                  fontWeight: '500',
                  fontSize: isMobile ? '14px' : '16px',
                }}>
                  {status.message}
                </p>
              </div>
            )}

            <form onSubmit={handleDemoRequest}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? '16px' : '24px',
              }}>
                <input
                  type="email"
                  placeholder="Work Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: isMobile ? '14px' : '16px',
                    fontSize: isMobile ? '15px' : '16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: colors.white,
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    opacity: loading ? 0.6 : 1,
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.primary}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
                />

                <input
                  type="text"
                  placeholder="Dispensary Name"
                  value={shopName}
                  onChange={(e) => setShopName(e.target.value)}
                  required
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: isMobile ? '14px' : '16px',
                    fontSize: isMobile ? '15px' : '16px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: colors.white,
                    outline: 'none',
                    transition: 'border-color 0.3s ease',
                    opacity: loading ? 0.6 : 1,
                  }}
                  onFocus={(e) => e.target.style.borderColor = colors.primary}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)'}
                />

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    background: loading 
                      ? 'rgba(16, 185, 129, 0.5)' 
                      : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%)`,
                    color: colors.white,
                    fontSize: isMobile ? '16px' : '18px',
                    fontWeight: '600',
                    padding: isMobile ? '16px' : '18px',
                    borderRadius: '8px',
                    border: 'none',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    marginTop: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease',
                    opacity: loading ? 0.7 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 10px 30px rgba(16, 185, 129, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  {loading ? (
                    <>
                      <div style={{
                        width: '20px',
                        height: '20px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      Sending...
                    </>
                  ) : (
                    <>
                      Book My Demo
                      <Calendar size={isMobile ? 18 : 20} />
                    </>
                  )}
                </button>

                <p style={{
                  fontSize: isMobile ? '12px' : '13px',
                  color: colors.grayLight,
                  marginTop: '8px',
                  margin: 0,
                }}>
                  We'll never share your info or spam you. Promise.
                </p>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{
        borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        padding: isMobile ? '48px 0' : '64px 0',
      }}>
        <div style={containerStyle}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: isMobile ? '24px' : '32px',
          }}>
            <h4 style={{
              fontSize: isMobile ? '24px' : '28px',
              fontWeight: '700',
              margin: 0,
            }}>
              Oak Compliance
            </h4>
            <p style={{
              color: colors.primary,
              fontWeight: '600',
              margin: 0,
              fontSize: isMobile ? '14px' : '16px',
            }}>
              🌲 Built in Massachusetts, for Massachusetts
            </p>
            <div style={{
              width: '100%',
              maxWidth: '400px',
              height: '1px',
              backgroundColor: 'rgba(255, 255, 255, 0.08)',
            }} />
            <div style={{
              display: 'flex',
              gap: isMobile ? '24px' : '32px',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}>
              {['Contact', 'Privacy', 'Terms'].map((link) => (
                <a
                  key={link}
                  href={`/${link.toLowerCase()}`}
                  style={{
                    color: colors.gray,
                    textDecoration: 'none',
                    transition: 'color 0.3s ease',
                    fontSize: isMobile ? '14px' : '16px',
                  }}
                  onMouseEnter={(e) => e.target.style.color = colors.primary}
                  onMouseLeave={(e) => e.target.style.color = colors.gray}
                >
                  {link}
                </a>
              ))}
            </div>
            <p style={{
              fontSize: isMobile ? '12px' : '13px',
              color: colors.grayLight,
              margin: 0,
            }}>
              © 2025 Oak Technologies, Inc. All rights reserved.
            </p>
            <p style={{
              fontSize: isMobile ? '11px' : '12px',
              color: colors.grayLight,
              maxWidth: '600px',
              fontStyle: 'italic',
              margin: 0,
              padding: isMobile ? '0 16px' : '0',
            }}>
              Oak Compliance is not affiliated with the Massachusetts Cannabis Control Commission. All regulatory references are for informational purposes only.
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Touch target improvements for mobile */
        @media (max-width: 768px) {
          button, a {
            -webkit-tap-highlight-color: rgba(16, 185, 129, 0.2);
          }
        }
      `}</style>
    </div>
  );
}
